import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConfirmAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [actionType, setActionType] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid confirmation link');
      return;
    }

    confirmAction(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const confirmAction = async (token) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/confirm-action?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message || 'Action confirmed successfully!');
        setActionType(data.action || '');
        
        // If password changed, redirect to login after delay
        if (data.action === 'password_change') {
          setTimeout(() => {
            navigate('/login?message=password_changed');
          }, 3000);
        }
      } else {
        setStatus('error');
        setMessage(data.detail || 'Failed to confirm action');
      }
    } catch (error) {
      setStatus('error');
      setMessage('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center text-2xl">
            {status === 'loading' && 'Confirming Action...'}
            {status === 'success' && '✅ Success!'}
            {status === 'error' && '❌ Error'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-center">
            {status === 'loading' && (
              <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
            )}
            {status === 'success' && (
              <CheckCircle className="w-16 h-16 text-green-500" />
            )}
            {status === 'error' && (
              <XCircle className="w-16 h-16 text-red-500" />
            )}
          </div>

          <p className="text-center text-slate-700">
            {message}
          </p>

          {status === 'success' && actionType === 'password_change' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-sm text-blue-800">
                  All your devices have been logged out for security. You'll be redirected to login in a moment.
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col space-y-2">
            {status === 'success' && actionType !== 'password_change' && (
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                Go to Dashboard
              </Button>
            )}
            
            {status === 'error' && (
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="w-full"
              >
                Back to Dashboard
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfirmAction;
